import { http } from '@app/lib/api'
import { useDebounced } from '@app/lib/hooks'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Icon } from './index'

interface Hit {
  entityType: string
  entityId: string
  title: string
  subtitle: string | null
  url: string | null
}

/** Admin-wide search across people, organizations, applications and more. */
export function GlobalSearch() {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const dq = useDebounced(q, 200)
  const navigate = useNavigate()
  const listId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const results = useQuery({
    queryKey: ['admin', 'search', dq],
    queryFn: () => http.get<{ items: Hit[] }>('/api/v1/admin/search', { q: dq }),
    enabled: dq.length >= 2,
  })
  const items = dq.length >= 2 ? (results.data?.items ?? []) : []
  const showList = open && dq.length >= 2

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const go = (h: Hit) => {
    if (h.url) navigate(h.url)
    setOpen(false)
    setQ('')
  }

  return (
    <div className="global-search">
      <label className="sr-only" htmlFor={`${listId}-input`}>
        Search everything
      </label>
      <div className="global-search__box">
        <Icon name="search" size={18} />
        <input
          ref={inputRef}
          id={`${listId}-input`}
          className="global-search__input"
          type="search"
          placeholder="Search (⌘K)"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
            setActive(0)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setActive((a) => Math.min(a + 1, items.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActive((a) => Math.max(a - 1, 0))
            } else if (e.key === 'Enter' && items[active]) {
              go(items[active] as Hit)
            } else if (e.key === 'Escape') {
              setOpen(false)
            }
          }}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && items[active] ? `${listId}-${active}` : undefined}
        />
      </div>
      <div className="global-search__results" hidden={!showList}>
        <div className="sr-only" role="status">
          {!showList
            ? ''
            : results.isLoading
              ? 'Searching'
              : `${items.length} ${items.length === 1 ? 'result' : 'results'}`}
        </div>
        {showList && results.isLoading ? (
          <div className="small muted global-search__status" aria-hidden="true">
            Searching…
          </div>
        ) : null}
        {showList && !results.isLoading && items.length === 0 ? (
          <div className="small muted global-search__status" aria-hidden="true">
            No results
          </div>
        ) : null}
        <div id={listId} role="listbox" aria-label="Search results">
          {items.map((h, i) => (
            // biome-ignore lint/a11y/useFocusableInteractive: focus stays in the combobox; aria-activedescendant tracks the option
            // biome-ignore lint/a11y/useKeyWithClickEvents: Enter/arrow keys are handled on the combobox input
            <div
              key={`${h.entityType}:${h.entityId}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={`global-search__option${i === active ? ' is-active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(h)}
              onMouseEnter={() => setActive(i)}
            >
              <span className="badge">{h.entityType}</span>
              <span className="bold">{h.title}</span>
              {h.subtitle ? <span className="small muted">{h.subtitle}</span> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
